import React from 'react';

import {
	AccountDisplayProps,
	EAccountDisplayState,
} from '@/components/AccountPanel/AccountPanel';
import { Chip } from '@/components/shared/Chip/Chip';
import {
	SettingCard,
	SettingSectionTitle,
	SettingsList,
} from '@/components/shared/SettingCard';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { useTeam } from '@/providers/TeamProvider';
import { isPlatform } from '@ionic/react';
import { List } from '@material-ui/core';

const isCapacitor = isPlatform('capacitor');

export const UsageAndLimits: React.FC<AccountDisplayProps> = ({
	pushDisplayState,
	setOpen,
}) => {
	const { services } = useServices();
	const { team } = useTeam();
	const {
		status: subscriptionStatus,
		setPaidModalOpen,
		chargebeeInstance,
		portalSessionRes,
		loadingChargebeeSession,
	} = useSubscription();

	const fireOpenBillingPortal = useAnalyticsEvent(
		'Usage_Open_Billing_Portal',
	);

	const showBilling = subscriptionStatus && portalSessionRes && !isCapacitor;

	const adminCount =
		team?.members.filter((m) => m.roles.includes('teamAdmin')).length ?? 0;

	const servicesAtLimit = services.length >= 1;
	const adminsAtLimit = adminCount >= 1;

	const limitChip = (text: string, atLimit: boolean) => (
		<Chip color={atLimit ? 'light-red' : 'light-gray'} variant="rounded">
			{text}
		</Chip>
	);

	return (
		<div className="p-lg">
			<List>
				<SettingCard
					type="setting"
					title="Plan"
					trailingComponent={
						<Chip color="light-gray" variant="rounded">
							{subscriptionStatus ? 'Leader' : 'Free'}
						</Chip>
					}
					noChevron
					orderPosition={showBilling ? 'first' : 'only'}
					onClick={() => setPaidModalOpen(true)}
				/>
				{showBilling && (
					<SettingCard
						type="setting"
						title="Billing"
						loading={loadingChargebeeSession}
						disabled={!portalSessionRes || loadingChargebeeSession}
						noChevron
						orderPosition="last"
						onClick={() => {
							fireOpenBillingPortal();
							setOpen({ open: false });
							const cbPortal =
								chargebeeInstance.createChargebeePortal();
							cbPortal.open({
								close() {
									//close callbacks
								},
							});
						}}
					/>
				)}
			</List>

			<SettingSectionTitle type="title" title="Plan" />
			<SettingsList
				items={[
					{
						type: 'setting',
						title: 'Services',
						trailingComponent: limitChip(
							`${services.length} of 1`,
							servicesAtLimit,
						),
						noChevron: true,
						onClick: () =>
							pushDisplayState(EAccountDisplayState.SERVICES),
					},
					{
						type: 'setting',
						title: 'Admins',
						trailingComponent: limitChip(
							`${adminCount} of 1`,
							adminsAtLimit,
						),
						noChevron: true,
						onClick: () =>
							pushDisplayState(EAccountDisplayState.TEAM),
					},
				]}
			/>
		</div>
	);
};

export default UsageAndLimits;
